import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { StorageConst } from '../shared/consts/storage-consts';
import { User } from '../shared/models/user.model';
import { DialogMessages, NgxBpChangePasswordService } from '@biggerpicture/ngx-bp-change-password';
import { SessionLanguageService } from '../session/services/session-language.service';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NgxBpMultiVersionService } from '@biggerpicture/ngx-bp-multi-version';

/**
 * This service can check if the user is logged in, initiate a login/logout requests, etc...
 */
@Injectable()
export class AuthService {
  private static get userRoutes(): any {
    return {
      /* isLoggedIn: '/user/isLoggedIn',*/
      /*login: '/user/login',*/
      isLoggedIn: '/api/sys/isLoggedIn',
      login: '/api/sys/login',
      logout: '/api/sys/logout',
      version: '/api/public/version'
    };
  }

  public betaFlags = {
    clientEnvironment: 1,
    adminAPIEnvironment: 1,
    siteAPIEnvironment: 1
  };
  public loginConfig;

  private _isLoggedInPromise: Promise<boolean> = null;

  private accessCode = new BehaviorSubject<string>('');
  public accessCode$ = this.accessCode.asObservable();

  // store the URL so we can redirect after logging in
  redirectUrl = '/';
  httpOptions;

  constructor(
    private http: HttpClient,
    private changePasswordService: NgxBpChangePasswordService,
    private multiVersionService: NgxBpMultiVersionService,
    private router: Router,
    private sessionLanguageService: SessionLanguageService
  ) {
    const currentUser = sessionStorage.getItem('TOKEN');
    this.httpOptions = new HttpHeaders({'Content-Type': 'application/json', Authorization: 'Bearer ' + currentUser});
  }

  async fetchCommonData(): Promise<void> {
    try {
      await this.getLoginConfig().toPromise().then(response => {
        if (response) {
          this.loginConfig = response;
        }
      });
    } catch (error) {
    }
  }

  getLoginConfig() {
    const confURL = environment.bucketUrl.replace(/\/assets$/, '') + '/login-config.json';
    const params = new HttpParams()
      .append('t', Math.random());
    return this.http.get<any>(confURL, { params });
  }

  /**
   * Returns a promise, whether the user is logged in or not
   * (if should present login page or allow him to navigate to the requested route).
   * It should be noted that this method is getting cached (only one request to the server), unless cache reset is fired.
   */
  isLoggedIn(): Promise<boolean> {
    if (!this._isLoggedInPromise) {
      this._isLoggedInPromise = this.http.get(`${environment.systemAPI}${AuthService.userRoutes.isLoggedIn}`,
        {headers: this.httpOptions}).toPromise().then((res: any) => {
        const user: User = res.list[0] || null;
        if (
          (sessionStorage.getItem(StorageConst.REDIRECTED_FROM_POS) && user) ||
          (sessionStorage.getItem(StorageConst.REDIRECTED_FROM_MULTI_VERSION) && user)
        ) {
          this.setIsLoggedInState(true);
          sessionStorage.setItem(StorageConst.USER, JSON.stringify(user));
        }
        const currentUser: any = JSON.parse(sessionStorage.getItem(StorageConst.USER));
        if (currentUser) {
          this.checkForBeta(currentUser);
        }
        return !!currentUser; // if token exist and user object not exist in the storage.
      }).catch(e => {
        return false;
      });
    }
    return this._isLoggedInPromise;
  }

  setIsLoggedInState(newState: boolean) {
    this._isLoggedInPromise = new Promise<boolean>((resolve => {
      resolve(newState);
    }));
  }

  getCurrentSiteId(): number {
    return JSON.parse(sessionStorage.getItem('USER'))?.siteId;
  }

  getCurrentSite(): { siteId: number, name: string } {
    return {
      siteId: this.getCurrentSiteId(),
      name: JSON.parse(sessionStorage.getItem('USER'))?.siteName
    };
  }

  isHoSite(): boolean {
    const user = JSON.parse(sessionStorage.getItem('USER'));
    return (user.siteId === user.hoSiteId && user.siteTypeId === 1) || user.siteTypeId === 3;
  }

  getHoSiteIdAndCode(): {hoSiteId: number, hoSiteCode: string} {
    return {
      hoSiteId: this.getHoSiteId(),
      hoSiteCode: JSON.parse(sessionStorage.getItem('USER'))?.hoSiteCode
    };
  }

  getSiteCode(): string {
    return JSON.parse(sessionStorage.getItem('USER'))?.siteCode;
  }

  getBusinessDate(): string {
    return JSON.parse(sessionStorage.getItem('USER'))?.businessDate;
  }

  getHoSiteId(): number {
    return Number(JSON.parse(sessionStorage.getItem('USER'))?.hoSiteId);
  }

  getClassId(): number {
    return Number(JSON.parse(sessionStorage.getItem('USER'))?.userClassId);
  }


  login(credentials: {
    accessCode: string,
    username: string,
    password: string,
    posId: string,
    newPassword?: string
  }): Observable<{ message: string, status: number }> {
    this.setIsLoggedInState(true);
    const body = {
      ...credentials,
      posSoftwareTypeId: 60
    };
    return this.http.post(
      `${environment.systemAPI}${AuthService.userRoutes.login}`,
      body,
      {withCredentials: environment.withCredentials}
    ).pipe(
      tap((response: any) => {
        if (response.token) {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          sessionStorage.setItem('TOKEN', response.token);
          sessionStorage.setItem('USER', JSON.stringify(response.user));
          this.checkForBeta(response.user);
          // Return to the originally-requested URL (e.g. a deep link like /invcounter?accessCode=XXX).
          // EXCEPTION: a POS/monitor auto-login URL carries x + y (+ accessCode). Navigating back to it
          // re-fires AuthGuard's auto-login (setAutoLogin) while the signin page is still alive, which
          // re-submits and loops. Those keep the original behaviour: go to '/' and let canActivateChild
          // route to the monitor. Only normal-login deep links (no x/y) return to their URL.
          let target = this.redirectUrl || '/';
          if (/[?&]x=/.test(target) && /[?&]y=/.test(target)) {
            target = '/';
          }
          this.redirectUrl = '/';
          sessionStorage.removeItem(StorageConst.RETURN_URL);
          // TOMIX-182: a fresh login gets exactly one default-menu-item redirect out of the
          // homepage; clear the flag here so it doesn't carry over from a previous session in
          // the same tab (e.g. logout then log back in without a full page reload).
          sessionStorage.removeItem(StorageConst.DEFAULT_MENU_ITEM_REDIRECTED);
          this.router.navigateByUrl(target);
        }
      }),
      catchError(error => {
        if (error?.error?.status === -25 && !body.newPassword) {
          this.changePasswordService.open({
            onSubmit: payload => (
              this.login({...body, ...payload})
            ),
            messages: this.changePasswordMessages()
          });
        }
        if (body.newPassword) {
          return throwError(this.changePasswordError(error));
        }
        if (error.status === 401) {
          return throwError(error.error);
        } else {
          return throwError({message: 'Unknown Error detected', status: error.status});
        }
      })
    );
  }

  private changePasswordMessages(): DialogMessages {
    const t = (key: string) => this.sessionLanguageService.translate(`CHANGE_PASSWORD.${key}`);
    return {
      title: t('TITLE'),
      oldPassword: t('OLD_PASSWORD'),
      newPassword: t('NEW_PASSWORD'),
      retypeNewPassword: t('RETYPE_NEW_PASSWORD'),
      patternError: t('POLICY_ERROR'),
      matchError: t('MATCH_ERROR'),
      cancelBtn: t('CANCEL'),
      saveBtn: t('SAVE')
    };
  }

  // The dialog renders error.message only, so it always needs a readable one.
  // -26 is the password policy rejection, whose API text is an unreadable rule dump.
  private changePasswordError(error: any): { message: string } {
    const apiError = error?.error;
    if (apiError?.status === -26) {
      return {message: this.sessionLanguageService.translate('CHANGE_PASSWORD.POLICY_ERROR')};
    }
    return {message: apiError?.message || this.sessionLanguageService.translate('CHANGE_PASSWORD.GENERIC_ERROR')};
  }

  logout(): Promise<any> {
    this.setIsLoggedInState(false);
    return this.http.get(`${environment.systemAPI}${AuthService.userRoutes.logout}`,
      {headers: this.httpOptions}).toPromise();
  }

  getSiteApiVersion() {
    return this.http
      .get(`${environment.systemAPI}${AuthService.userRoutes.version}`, {responseType: 'text'});
  }

  setAccessCode(accessCode: string): void {
    this.accessCode.next(accessCode);
  }

  checkForBeta(user: User): void {
    this.betaFlags.clientEnvironment = Number.isInteger(user?.clientEnvironment) ? user?.clientEnvironment : 1;
    this.betaFlags.adminAPIEnvironment = Number.isInteger(user?.adminAPIEnvironment) ? user?.adminAPIEnvironment : 1;
    this.betaFlags.siteAPIEnvironment = Number.isInteger(user?.siteAPIEnvironment) ? user?.siteAPIEnvironment : 1;
    this.multiVersionService.check(this.betaFlags.clientEnvironment, {
      autoLoginToken: sessionStorage.getItem('TOKEN'),
      posId: sessionStorage.getItem('POS_ID')
    });
  }
}
